import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {message} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {getUserInfo, hasHeadquartersAccess} from "../../../../utils/auth";

import {OrderWithAddressType} from "../hooks/useWmsExceptionQuery";
import {MARK_AS_EXCEPTION} from '../../../../graphql/mutations/markAsException';
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../graphql/queries/getChannelAll';
import MarkAsExceptionModal, {MarkAsExceptionData} from "../../../../components/MarkAsExceptionModal";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import {overallStatusOptions} from "../../../../config/filterOptions";
import { useStatusInternationalization } from '../../../delivery/parcels/hooks/useStatusInternationalization';
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";
import {ExceptionDictionaryResponse,ExceptionDictionaryType, GET_ALL_EXCEPTION} from "../graphql/queries/getAllException";
 
import { GET_NEXT_ACTION_LIST, NextActionListResponse, NextActionListType } from '../../../../graphql/queries/getNextActionList';
import { isHubManager, isWarehouseManager } from '../../../../utils/auth';
import { SiteType } from 'graphql/types/SiteType';
import {
    GET_WAREHOUSE_LIST_FOR_OPTION_BY_HUB_ID,
    GetWarehouseListByHubIdResponse
} from 'graphql/queries/warehouseQueries';
import {useWarehouseContext} from "../../../../contexts/WarehouseContext";
import { useTranslation } from 'react-i18next';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { getLastWeekDateRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';

interface WmsExceptionFilterProps {
    filters: OrderFiltersType;
    setFilters: (filters: OrderFiltersType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: OrderWithAddressType[];
}

interface CustomerOption {
    value: string;
    label: string;
}

const WmsExceptionFilter: React.FC<WmsExceptionFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
    selectedRows,
}) => {
    const {t} = useTranslation();
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    // - undefined：未初始化，默认最近一周
    // - null：用户手动清空（点击叉号），保持为空
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedMarkExceptionAtRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters,
        timeKey: 'markExceptionAt',
        hasUserEditedTimeRef: hasUserEditedMarkExceptionAtRef,
    });

    const getEffectiveMarkExceptionAt = (value: any): [string | null, string | null] | null => {
        if (value === null) return null;
        const isValid = value &&
            Array.isArray(value) &&
            value.length === 2;
        return isValid ? (value as [string | null, string | null]) : getLastWeekDateRange();
    };
    const [markAsExceptionVisible, setMarkAsExceptionVisible] = useState(false);
    const [markAsExceptionLoading, setMarkAsExceptionLoading] = useState(false);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    const userRole = getUserInfo()?.account?.role?.name;
    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const { getExceptionReasonText, getNextActionTextFromBackend, exceptionOrderStatusOptions } = useStatusInternationalization();
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const {data:nextActionData} = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST);
    const exceptionList = exceptionData?.getExceptionDictionaryAll || [];
    const nextActionSelectData: NextActionListType[] = nextActionData?.getNextActionList || [];
    const isHubManagerRole = isHubManager();
    const isWarehouseManagerRole = isWarehouseManager();
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
    const hubId = siteType === SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId;
    const { data: warehouseByIdData } = useQuery<GetWarehouseListByHubIdResponse>(GET_WAREHOUSE_LIST_FOR_OPTION_BY_HUB_ID, {
        variables: {
            hubId: hubId
        },
        skip: !hubId || siteType === SiteType.WAREHOUSE
    });

    // 定义每列的标签数组，用于列对齐计算
    // 注意：Tracking 虽然独占一行，但仍属于第一列，需纳入第一列宽度计算以保证对齐
    const labelTracking = t('wms.exceptions.filters.tracking');
    const column1Labels = [
        labelTracking,
        t('wms.exceptions.filters.client'),
        t('wms.exceptions.filters.exceptionReason'),
        t('wms.exceptions.filters.nextAction')
    ];
    const column2Labels = [t('wms.exceptions.filters.exceptionTime'), t('wms.exceptions.filters.exceptionPlaceWhs')];
    const column3Labels = [t('wms.exceptions.filters.parcelStatus'), t('wms.exceptions.filters.exceptionStatus')];

    const exceptionReasonOptions = exceptionList.map(ex => ({
        value: ex.exceptionReasonCode,
        label: String(getExceptionReasonText(ex, ex.exceptionReasonCode))
    }));

    useEffect(() => {
        if (data?.getAllChannels) {
            const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id?.toString() || '',  // 确保value是字符串类型
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, exceptionData, userRole, siteType]);
    
    // 处理时间变化的函数
    const handleExceptionTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedMarkExceptionAtRef.current = true;
        setFilters({...filters, markExceptionAt: dates});
    };

    const handleMarkAsExceptionConfirm = async (formData: MarkAsExceptionData) => {
        try {
            setMarkAsExceptionLoading(true);
            await markAsException({
                variables: {
                    orderId: selectedRows[0].id,
                    exceptionReasonCode: formData.exceptionReasonCode,
                    exceptionHubId: formData.exceptionHubId,
                    exceptionWarehouseId: formData.exceptionWarehouseId,
                    exceptionImageS3KeysJson: formData.photos
                }
            })

            message.success(t('wms.exceptions.messages.markingExceptionSuccessfully'));
            setMarkAsExceptionVisible(false);
            handleFilter();
        } catch (error) {
            console.error('Marking exception failed:', error);
            message.error(t('wms.exceptions.messages.markingExceptionFailed'));
        } finally {
            setMarkAsExceptionLoading(false);
        }
    };

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="flex flex-col lg:flex-row gap-4">
                {/*  异常相关的筛选条件 - 优化三等分布局 */}
                <div className="flex-1">
                {/* Tracking 独占一行 */}
                <div className="mb-4">
                    <BatchInputFilterItem
                        label={labelTracking}
                        value={filters.trackingNumbers}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                            ...filters,
                            trackingNumbers: e.target.value || undefined
                        })}
                        placeholder={t('wms.parcels.placeholders.trackingNumbers') || ''}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                        rows={2}
                    />
                </div>

                {/* 使用 Grid 布局确保列对齐（三等分） */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-10 gap-y-4 items-start">
                    {/* 第一列 */}
                    <div className="min-w-0 space-y-4">
                        <ParcelFilterStyle
                            label={t('wms.exceptions.filters.client')}
                            options={channelOptions}
                            value={(() => {
                                // 解决:选中Option后,显示label，Chrome F5刷新后，显示ID的问题
                                const currentValue = filters.channelId;
                                if (!currentValue) {
                                    return undefined;
                                }
                                if (!data?.getAllChannels) {
                                    return undefined;
                                }
                                return currentValue?.toString() || '';
                            })()}
                            onChange={(value: string) => setFilters({...filters, channelId: value ? [value] : undefined})}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(column1Labels)}
                        />

                        <ParcelFilterStyle
                            label={t('wms.exceptions.filters.exceptionReason')}
                            options={exceptionReasonOptions}
                            value={filters.exceptionReasonCodes ?? []}
                            onChange={(values: string[]) => setFilters({...filters, exceptionReasonCodes: values && values.length > 0 ? values : undefined})}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(column1Labels)}
                            mode="multiple"
                            showSearch
                            placeholder={t('wms.exceptions.filters.all') || undefined}
                        />

                        <ParcelFilterStyle
                            label={t('wms.exceptions.filters.nextAction')}
                            options={nextActionSelectData.filter(item => item.value !== 0).map(option => ({
                                value: option.name,
                                label: getNextActionTextFromBackend(option.name)
                            }))}
                            value={filters.nextAction ?? undefined}
                            onChange={(value: string) => setFilters({...filters, nextAction: value || undefined})}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(column1Labels)}
                            placeholder={t('components.input.select') as string}
                        />
                    </div>

                    {/* 第二列 */}
                    <div className="min-w-0 space-y-4">
                        <DateRangeFilterItem
                            label={t('wms.exceptions.filters.exceptionTime')}
                            value={getEffectiveMarkExceptionAt(filters.markExceptionAt)}
                            onChange={handleExceptionTimeChange}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(column2Labels)}
                        />

                        {(isHubManagerRole || hasHeadquartersAccess()) && (
                            <ParcelFilterStyle
                                label={t('wms.exceptions.filters.exceptionPlaceWhs')}
                                options={[
                                    { value: '0', label: t('wms.exceptions.filters.na') },
                                    ...(warehouseByIdData?.warehouseList?.map(warehouse => ({
                                        value: warehouse.id?.toString() || '',
                                        label: warehouse.name as string
                                    })) || [])
                                ]}
                                value={(() => {
                                    // 解决:选中Option后,显示US，Chrome F5刷新后，显示0的问题(value:0,label:'US')
                                    const currentValue = filters.exceptionWarehouseId;
                                    if (currentValue === '0' || Number(currentValue) === 0) {
                                        return '0';
                                    }
                                    if (!currentValue && currentValue !== '0' && Number(currentValue) !== 0) {
                                        return undefined;
                                    }
                                    if (!warehouseByIdData?.warehouseList) {
                                        return undefined;
                                    }
                                    return currentValue?.toString() || '';
                                })()}
                                onChange={(value: string) => setFilters({...filters, exceptionWarehouseId: value || undefined})}
                                className="w-full"
                                spanClassName={getSmartLabelClassName(column2Labels)}
                            />
                        )}
                    </div>

                    {/* 第三列 */}
                    <div className="min-w-0 space-y-4">
                        <ParcelFilterStyle
                            label={t('wms.exceptions.filters.parcelStatus')}
                            options={overallStatusOptions}
                            mode="multiple"
                            maxTagCount="responsive"
                            value={filters.overallStatus ?? []}
                            onChange={(values: string[]) => setFilters({
                                ...filters,
                                overallStatus: values && values.length > 0 ? values : undefined
                            })}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(column3Labels)}
                        />

                        <ParcelFilterStyle
                            label={t('wms.exceptions.filters.exceptionStatus')}
                            options={exceptionOrderStatusOptions}
                            mode="multiple"
                            maxTagCount="responsive"
                            value={filters?.exceptionStatuses?.map(String) ?? []}
                            onChange={(values: string[]) => setFilters({
                                ...filters,
                                exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined
                            })}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(column3Labels)}
                        />

                    </div>
                </div>
                </div>
                </div>
            

            {/* 异常标记弹窗（单个：可能已经废弃） */}
            <MarkAsExceptionModal
                visible={markAsExceptionVisible}
                onClose={() => setMarkAsExceptionVisible(false)}
                onConfirm={handleMarkAsExceptionConfirm}
                loading={markAsExceptionLoading}
                parcel={selectedRows[0] as any}
                userRole={userRole || ''}
            />
        </div>
    );
};

export default WmsExceptionFilter; 